import {
  getDatabase,
  ref,
  get,
  set,
  remove,
  update,
  push,
  query,
  orderByKey,
  limitToLast,
} from "firebase/database";

// Centralize saved posts path to keep /users light
const getSavedPostsPath = (userId) => `savedPosts/${userId}`;
const getLegacySavedPostsPath = (userId) => `users/${userId}/savedPosts`;
const SAVED_POSTS_FETCH_LIMIT = 100; // keep payload small while preserving recent history

/**
 * Collect post data for analytics (NEW - Data Collection)
 * @param {Object} postData - Complete post data
 * @returns {Promise<Object>} - Result of data collection
 */
export const collectPostData = async (postData) => {
  try {
    const db = getDatabase();
    const userId = localStorage.getItem("userId");
    const now = Date.now();

    // Generate post ID if not provided
    const postId = postData.postId || now.toString();

    // Structure data for collection
    const collectionData = {
      postId: postId,
      userId: userId,
      createdAt: now,
      // expiresAt: sevenDaysFromNow, // Removed - data will be stored permanently

      // User input data
      input: {
        userPrompt: postData.userPrompt || "",
        domain: postData.domain || "",
        platform: postData.platform || "",
        domainFields: postData.domainFields || {},
        referenceUrl: postData.referenceUrl || null,
        referenceDocuments: postData.referenceDocuments || [],
        postSettings: postData.postSettings || {},
        selectedTopics: postData.selectedTopics || [],
      },

      // AI output data
      output: {
        generatedContent: postData.generatedContent || "",
        generatedImages: postData.generatedImages || [],
        generatedHashtags: postData.generatedHashtags || [],
        modelUsed: "gemini-2.0-flash",
        generatedAt: now,
      },

      // User interaction tracking
      edits: [],
      published: false,
      publishedUrl: null,
      publishedAt: null,

      // Analytics
      editCount: 0,
      viewCount: 0,
      lastViewedAt: now,
    };

    // Save to Firebase
    const postsRecentRef = ref(db, `users/${userId}/posts_recent/${postId}`);
    await set(postsRecentRef, collectionData);

    console.log("✅ Data collected for post:", postId);
    return { success: true, postId };
  } catch (error) {
    console.error("Error collecting post data:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Track user edit to a post (NEW - Data Collection)
 * @param {string} postId - Post ID
 * @param {Object} editData - Edit information
 * @returns {Promise<boolean>} - Success status
 */
export const trackUserEdit = async (postId, editData) => {
  try {
    const db = getDatabase();
    const userId = localStorage.getItem("userId");

    const edit = {
      timestamp: Date.now(),
      editType: editData.editType || "unknown",
      before: editData.before || null,
      after: editData.after || null,
      position: editData.position || null,
      fieldChanged: editData.fieldChanged || null,
    };

    // Append edit to edits array
    const editsRef = ref(db, `users/${userId}/posts_recent/${postId}/edits`);
    await push(editsRef, edit);

    // Increment edit count
    const postRef = ref(db, `users/${userId}/posts_recent/${postId}`);
    const snapshot = await get(postRef);
    if (snapshot.exists()) {
      const currentEditCount = snapshot.val().editCount || 0;
      await update(postRef, {
        editCount: currentEditCount + 1,
        lastEditedAt: Date.now(),
      });
    }

    console.log("✅ Edit tracked for post:", postId);
    return true;
  } catch (error) {
    console.error("Error tracking user edit:", error);
    return false;
  }
};

/**
 * Mark post as published (NEW - Data Collection)
 * @param {string} postId - Post ID
 * @param {Object} publishInfo - Publication information
 * @returns {Promise<boolean>} - Success status
 */
export const markPostPublished = async (postId, publishInfo) => {
  try {
    const db = getDatabase();
    const userId = localStorage.getItem("userId");

    const postRef = ref(db, `users/${userId}/posts_recent/${postId}`);
    await update(postRef, {
      published: true,
      publishedUrl: publishInfo.url || null,
      publishedAt: Date.now(),
      publishPlatform: publishInfo.platform || null,
      fanpageId: publishInfo.fanpageId || null,
    });

    console.log("✅ Post marked as published:", postId);
    return true;
  } catch (error) {
    console.error("Error marking post as published:", error);
    return false;
  }
};

/**
 * Save post link to Firebase
 * @param {string} fanpageId - Fanpage ID
 * @param {string} postId - Post ID
 * @returns {Promise<boolean>} - Success status
 */
export const savePostLink = async (fanpageId, postId) => {
  try {
    const db = getDatabase();
    const userId = localStorage.getItem("userId");
    const timestamp = Date.now(); // Lấy timestamp hiện tại
    const postLink = `https://www.facebook.com/${fanpageId}/posts/${postId}`;
    const formattedLink = `${timestamp}|${postLink}`; // Định dạng mới: timestamp|link
    const postType = "normalPostGenerator"; // Loại bài đăng
    const postLinksRef = ref(db, `users/${userId}/posts/${postType}/links`);

    // Lấy danh sách links hiện tại
    const snapshot = await get(postLinksRef);
    const currentLinks = snapshot.exists() ? snapshot.val() : [];

    // Thêm link mới vào đầu mảng
    const updatedLinks = [formattedLink, ...currentLinks];

    // Lưu lại vào database
    await set(postLinksRef, updatedLinks);

    return true;
  } catch (error) {
    console.error("Error saving post link:", error);
    return false;
  }
};

/**
 * Fetch count from Firebase
 * @param {string} path - Path to count
 * @returns {Promise<number>} - Count value
 */
export const fetchCount = async (path) => {
  try {
    const db = getDatabase();
    const userId = localStorage.getItem("userId");
    const countRef = ref(
      db,
      `users/${userId}/statistical/normalPostGenerator/${path}`
    );
    const snapshot = await get(countRef);

    if (snapshot.exists()) {
      return snapshot.val();
    }
    return 0;
  } catch (error) {
    console.error(`Error fetching ${path} count:`, error);
    return 0;
  }
};

/**
 * Lưu bài viết vào Firebase
 * @param {Object} postData - Dữ liệu bài viết
 * @returns {Promise<Object>} - Kết quả lưu bài viết
 */
export const savePostToFirebase = async (postData) => {
  try {
    const db = getDatabase();
    const userId = localStorage.getItem("userId");
    const postId = Date.now().toString();
    const savedPostRef = ref(db, `${getSavedPostsPath(userId)}/${postId}`);

    // Thêm timestamp và id vào dữ liệu bài viết
    const savedPostData = {
      ...postData,
      id: postId,
      savedAt: postId,
    };

    await set(savedPostRef, savedPostData);
    // Clean legacy path so /users payload is small
    try {
      await remove(ref(db, `${getLegacySavedPostsPath(userId)}/${postId}`));
    } catch (cleanupError) {
      console.warn("Could not clean legacy saved post path:", cleanupError);
    }
    return { success: true, postId };
  } catch (error) {
    console.error("Lỗi khi lưu bài viết:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Lấy danh sách bài viết đã lưu từ Firebase
 * @returns {Promise<Object>} - Danh sách bài viết đã lưu
 */
export const getSavedPosts = async () => {
  try {
    const db = getDatabase();
    const userId = localStorage.getItem("userId");
    const savedPostsRef = query(
      ref(db, getSavedPostsPath(userId)),
      orderByKey(),
      limitToLast(SAVED_POSTS_FETCH_LIMIT)
    );
    let snapshot = await get(savedPostsRef);

    if (snapshot.exists()) {
      return { success: true, posts: snapshot.val() };
    }

    // Fallback to legacy path for old data (also limited)
    const legacyRef = query(
      ref(db, getLegacySavedPostsPath(userId)),
      orderByKey(),
      limitToLast(SAVED_POSTS_FETCH_LIMIT)
    );
    snapshot = await get(legacyRef);

    if (snapshot.exists()) {
      return { success: true, posts: snapshot.val() };
    }

    return { success: true, posts: {} };
  } catch (error) {
    console.error("Lỗi khi lấy danh sách bài viết đã lưu:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Đếm số lượng bài viết đã lưu của người dùng
 * @returns {Promise<number>} - Số lượng bài viết đã lưu
 */
export const countSavedPosts = async () => {
  try {
    const result = await getSavedPosts();
    if (result.success) {
      return Object.keys(result.posts).length;
    }
    return 0;
  } catch (error) {
    console.error("Lỗi khi đếm số lượng bài viết đã lưu:", error);
    return 0;
  }
};

/**
 * Tăng số lượng bài viết đã tạo trong Firebase để thống kê
 * @returns {Promise<Object>} - Kết quả tăng count
 */
export const incrementCreatedPostCount = async () => {
  try {
    const db = getDatabase();
    const userId = localStorage.getItem("userId");
    const today = new Date().toISOString().split("T")[0]; // Format: YYYY-MM-DD

    // Đường dẫn lưu thống kê tạo bài viết theo ngày
    const dailyCountRef = ref(
      db,
      `users/${userId}/statistical/normalPostGenerator/dailyCreatedCounts/${today}`
    );
    const totalCountRef = ref(
      db,
      `users/${userId}/statistical/normalPostGenerator/totalCreatedCount`
    );

    // Lấy count hiện tại
    const dailySnapshot = await get(dailyCountRef);
    const totalSnapshot = await get(totalCountRef);

    const currentDailyCount = dailySnapshot.exists() ? dailySnapshot.val() : 0;
    const currentTotalCount = totalSnapshot.exists() ? totalSnapshot.val() : 0;

    // Cập nhật cả daily count và total count
    const updates = {};
    updates[
      `users/${userId}/statistical/normalPostGenerator/dailyCreatedCounts/${today}`
    ] = currentDailyCount + 1;
    updates[
      `users/${userId}/statistical/normalPostGenerator/totalCreatedCount`
    ] = currentTotalCount + 1;
    updates[`users/${userId}/statistical/normalPostGenerator/lastCreatedAt`] =
      Date.now();

    await update(ref(db), updates);

    console.log(
      `✅ Đã tăng count tạo bài viết: Daily=${currentDailyCount + 1}, Total=${
        currentTotalCount + 1
      }`
    );
    return {
      success: true,
      dailyCount: currentDailyCount + 1,
      totalCount: currentTotalCount + 1,
    };
  } catch (error) {
    console.error("❌ Lỗi khi tăng count tạo bài viết:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Lấy số lượng bài viết đã tạo từ Firebase
 * @param {string} type - Loại count cần lấy ('daily', 'total', 'monthly')
 * @param {string} date - Ngày cụ thể (dành cho daily) hoặc tháng (dành cho monthly), format: YYYY-MM-DD hoặc YYYY-MM
 * @returns {Promise<number>} - Số lượng bài viết
 */
export const getCreatedPostCount = async (type = "total", date = null) => {
  try {
    const db = getDatabase();
    const userId = localStorage.getItem("userId");

    let countRef;
    switch (type) {
      case "daily":
        const targetDate = date || new Date().toISOString().split("T")[0];
        countRef = ref(
          db,
          `users/${userId}/statistical/normalPostGenerator/dailyCreatedCounts/${targetDate}`
        );
        break;
      case "monthly":
        const targetMonth = date || new Date().toISOString().slice(0, 7); // YYYY-MM
        // Để lấy monthly count, ta sẽ tính tổng tất cả ngày trong tháng
        const monthlyRef = ref(
          db,
          `users/${userId}/statistical/normalPostGenerator/dailyCreatedCounts`
        );
        const monthlySnapshot = await get(monthlyRef);
        if (monthlySnapshot.exists()) {
          const dailyCounts = monthlySnapshot.val();
          let monthlyTotal = 0;
          Object.keys(dailyCounts).forEach((dateKey) => {
            if (dateKey.startsWith(targetMonth)) {
              monthlyTotal += dailyCounts[dateKey] || 0;
            }
          });
          return monthlyTotal;
        }
        return 0;
      case "total":
      default:
        countRef = ref(
          db,
          `users/${userId}/statistical/normalPostGenerator/totalCreatedCount`
        );
        break;
    }

    const snapshot = await get(countRef);
    return snapshot.exists() ? snapshot.val() : 0;
  } catch (error) {
    console.error(`❌ Lỗi khi lấy ${type} count:`, error);
    return 0;
  }
};

/**
 * Xóa bài viết đã lưu từ Firebase
 * @param {string} postId - ID bài viết
 * @returns {Promise<Object>} - Kết quả xóa bài viết
 */
export const deleteSavedPost = async (postId) => {
  try {
    const db = getDatabase();
    const userId = localStorage.getItem("userId");
    const savedPostRef = ref(db, `${getSavedPostsPath(userId)}/${postId}`);

    await remove(savedPostRef);
    // Also clean legacy copy if any
    try {
      await remove(ref(db, `${getLegacySavedPostsPath(userId)}/${postId}`));
    } catch (cleanupError) {
      console.warn("Could not clean legacy saved post path:", cleanupError);
    }
    return { success: true };
  } catch (error) {
    console.error("Lỗi khi xóa bài viết đã lưu:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Auto-save generated post to Firebase (không phụ thuộc vào user action)
 * @param {Object} postData - Post data from generation
 * @param {Object} options - Form options (domain, projectType, requirements, etc)
 * @param {Array} formattedPosts - Generated posts array
 * @param {string} source - Source type ('normal' or 'auto')
 * @returns {Promise<Object>} - Result of auto-save
 */
export const autoSaveGeneratedPost = async (
  postData,
  options,
  formattedPosts,
  source = "normal"
) => {
  try {
    const db = getDatabase();
    const userId = localStorage.getItem("userId");
    if (!userId) return { success: false, error: "No userId found" };

    const now = Date.now();
    const autoPostId = now.toString();

    // Prepare context files metadata (only name and type, not content)
    const contextFilesMetadata = postData.contextFiles?.map((file) => ({
      name: file.name,
      type: file.type,
    })) || [];

    // Build auto-save data
    const autoSaveData = {
      postId: autoPostId,
      source: source,
      createdAt: now,

      // Input data
      input: {
        userPrompt: options.requirements || "",
        domain: options.domainName || "",
        platform: options.publishingPlatform?.id || options.publishingPlatform || 1,
        domainFields: {
          projectType: options.projectType || "",
          contentType: options.selectedContentType || "",
          publishingPlatform: options.publishingPlatform,
          targetCustomer: options.targetCustomer || "",
        },
        referenceUrl: options.referenceUrl || null,
        contextFilesMetadata: contextFilesMetadata,
        postSettings: {
          postLength: options.postLength || "",
          postStyles: options.postStyles || [],
          includeContactInfo: options.includeContactInfo || false,
          allowNoImage: postData.allowNoImage || false,
          imageSource: postData.imageSource || "internet",
        },
      },

      // AI output data
      output: {
        generatedContent: formattedPosts[0]?.text || "",
        generatedImages: formattedPosts[0]?.image ? [formattedPosts[0].image] : [],
        modelUsed: "gemini-2.0-flash",
        generatedAt: now,
        alternativeVersions: formattedPosts.length > 1 ? formattedPosts.length : 0,
      },

      // Tracking
      edits: [],
      published: false,
      publishedUrl: null,
      publishedAt: null,
      publishPlatform: null,
      fanpageId: null,

      // Analytics
      editCount: 0,
      viewCount: 0,
      lastViewedAt: now,
    };

    // Save to Firebase
    const generatedPostRef = ref(
      db,
      `users/${userId}/generated_posts/${autoPostId}`
    );
    await set(generatedPostRef, autoSaveData);

    console.log("✅ Auto-saved generated post:", autoPostId);
    return { success: true, postId: autoPostId };
  } catch (error) {
    console.error("❌ Error auto-saving generated post:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Mark auto-saved post as published
 * @param {string} postId - Auto-saved post ID
 * @param {Object} publishInfo - Publication information
 * @returns {Promise<boolean>} - Success status
 */
export const markAutoSavedPostPublished = async (postId, publishInfo) => {
  try {
    const db = getDatabase();
    const userId = localStorage.getItem("userId");
    if (!userId) return false;

    const postRef = ref(db, `users/${userId}/generated_posts/${postId}`);
    await update(postRef, {
      published: true,
      publishedUrl: publishInfo.url || null,
      publishedAt: Date.now(),
      publishPlatform: publishInfo.platform || null,
      fanpageId: publishInfo.fanpageId || null,
    });

    console.log("✅ Marked auto-saved post as published:", postId);
    return true;
  } catch (error) {
    console.error("❌ Error marking auto-saved post as published:", error);
    return false;
  }
};
